/**
 * "Carers Are Special People. What Kind of Carer Are You?" — reflection quiz.
 *
 * Content is verbatim from the approved brief (JCC_Carers_Are_Special_People_Quiz.md,
 * revised content read 15 October 2026). The answer letters change meaning between
 * questions — see the per-question `theme` on each option, taken directly from the
 * approved scoring table.
 *
 * R = Reassurer, O = Organiser, N = Observer, V = Advocate, X = a habit to examine
 * (never a fifth personality type, never subtracted from the other totals).
 *
 * Everything here is pure data + pure functions — no network calls, no storage.
 */

export const QUESTIONS = [
  {
    id: 1,
    text: "You are starting a placement. The handover contains plenty of information, but you have limited time before the morning routine. Where do you begin?",
    options: [
      { letter: "A", theme: "V", text: "Ask the person what matters most to them this morning and how they would like your help." },
      { letter: "B", theme: "X", text: "Establish a familiar morning pattern from previous placements; consistency usually helps people settle." },
      { letter: "C", theme: "R", text: "Spend a few minutes building a connection so the person feels comfortable with someone new." },
      { letter: "D", theme: "O", text: "Check the care plan, timings and practical information so the essential tasks are organised." },
      { letter: "E", theme: "N", text: "Compare the handover with what you notice about the person, clarifying anything that does not match." },
    ],
  },
  {
    id: 2,
    text: "Breakfast is taking longer than expected. The person wants to prepare it themselves and can do so within the agreed safe arrangements. What is your first instinct?",
    options: [
      { letter: "A", theme: "O", text: "Adjust the setup and order of the remaining tasks to make room for them to finish." },
      { letter: "B", theme: "N", text: "Notice which parts are taking longer and ask whether a particular step needs support." },
      { letter: "C", theme: "R", text: "Offer reassurance that there is time and they do not need to feel rushed." },
      { letter: "D", theme: "X", text: "Finish the fiddlier parts for them; keeping the morning on track will avoid stress later." },
      { letter: "E", theme: "V", text: "Ask which parts they want help with, leaving the decision with them." },
    ],
  },
  {
    id: 3,
    text: "Your shift is ending. Nothing dramatic has happened, but the person ate less and seemed quieter than usual. What would your handover emphasise?",
    options: [
      { letter: "A", theme: "N", text: "The changes you observed, with enough detail for the next carer to follow up appropriately." },
      { letter: "B", theme: "R", text: "How the person seemed to feel and what helped them engage with you." },
      { letter: "C", theme: "X", text: "The essential tasks and a brief reassurance that the day was settled; the next carer can judge how they seem." },
      { letter: "D", theme: "V", text: "What the person said they wanted, including anything they felt had not been addressed." },
      { letter: "E", theme: "O", text: "A clear record of food, fluids and outstanding tasks so the next shift knows what needs following up." },
    ],
  },
  {
    id: 4,
    text: "The person says they dislike the usual washing routine. You have followed it for weeks and it fits the timetable well. What do you do first?",
    options: [
      { letter: "A", theme: "O", text: "Work out an alternative order or time that could fit the day's practical needs." },
      { letter: "B", theme: "X", text: "Explain why the familiar routine works and encourage them to try it again before changing things." },
      { letter: "C", theme: "V", text: "Ask what they would prefer and whether they felt involved in agreeing the routine." },
      { letter: "D", theme: "R", text: "Listen to how the routine makes them feel, without rushing them towards a solution." },
      { letter: "E", theme: "N", text: "Explore what specifically bothers them and whether anything has changed recently." },
    ],
  },
  {
    id: 5,
    text: "A family asks you to carry out an unfamiliar care task. They say another carer does it and offer to show you. What is closest to your response?",
    options: [
      { letter: "A", theme: "R", text: "Explain gently that you need the appropriate training and guidance, acknowledging why they want help." },
      { letter: "B", theme: "N", text: "Look closely at what the task involves and clarify the limits of your competence before proceeding." },
      { letter: "C", theme: "V", text: "State clearly that a family demonstration cannot replace the training or assessment you need." },
      { letter: "D", theme: "X", text: "Watch their demonstration and decide whether it looks manageable; you would stop if you felt out of your depth." },
      { letter: "E", theme: "O", text: "Arrange clarification of responsibilities, training and assessment before agreeing to add the task." },
    ],
  },
  {
    id: 6,
    text: "Several days have felt difficult, with repeated disagreements about the routine. When you reflect, where does your attention go first?",
    options: [
      { letter: "A", theme: "V", text: "Whether the person's wishes and your working boundaries have been properly heard." },
      { letter: "B", theme: "N", text: "Patterns in when disagreements arise and what happens immediately beforehand." },
      { letter: "C", theme: "R", text: "How everyone has been feeling and whether the relationship needs more reassurance." },
      { letter: "D", theme: "O", text: "Which expectations, timings or practical arrangements need a clearer agreement." },
      { letter: "E", theme: "X", text: "Whether the family needs a clearer explanation of your approach so they stop questioning familiar methods." },
    ],
  },
  {
    id: 7,
    text: "You realise you have made a mistake that could affect care. Which response is closest to what you would do?",
    options: [
      { letter: "A", theme: "O", text: "Attend to immediate safety, report promptly through the appropriate route and help put a practical prevention step in place." },
      { letter: "B", theme: "X", text: "Correct what you can and monitor the person; if they seem fine, raising it may create unnecessary worry." },
      { letter: "C", theme: "R", text: "Attend to immediate safety and report promptly, making sure the person receives appropriate explanation and reassurance." },
      { letter: "D", theme: "N", text: "Attend to immediate safety and report promptly, recording the facts accurately rather than relying on memory." },
      { letter: "E", theme: "V", text: "Attend to immediate safety and report promptly, also raising any pressure or unclear instruction that contributed." },
    ],
  },
  {
    id: 8,
    text: "Two placements appear suitable for your skills, pay and working requirements. What would most influence your choice?",
    options: [
      { letter: "A", theme: "O", text: "Clear routines, responsibilities and reliable arrangements for handovers and breaks." },
      { letter: "B", theme: "N", text: "A person and family who welcome questions and thoughtful observations about changes in care." },
      { letter: "C", theme: "X", text: "Freedom to establish your own way of working without frequent discussion about small preferences." },
      { letter: "D", theme: "R", text: "The opportunity to build a warm and trusting relationship with the person." },
      { letter: "E", theme: "V", text: "An arrangement where the person has a clear voice and everyone can discuss expectations openly." },
    ],
  },
  {
    id: 9,
    text: "Someone gives you constructive feedback that you did not expect. What would you most likely do first?",
    options: [
      { letter: "A", theme: "N", text: "Ask for a specific example so you can understand what happened." },
      { letter: "B", theme: "V", text: "Discuss their concern openly, including any disagreement, while listening to their perspective." },
      { letter: "C", theme: "R", text: "Think about how your approach felt to the person and acknowledge their experience." },
      { letter: "D", theme: "X", text: "Explain the experience behind your method so they understand why you used it, before considering a change." },
      { letter: "E", theme: "O", text: "Clarify what needs adjusting and agree a practical next step." },
    ],
  },
  {
    id: 10,
    text: "A difficult experience has left you needing advice. How would you approach it?",
    options: [
      { letter: "A", theme: "O", text: "Use the agreed confidential advice route and organise the relevant information before asking for help." },
      { letter: "B", theme: "R", text: "Seek appropriate confidential support, focusing on the effect on the person and on your own wellbeing." },
      { letter: "C", theme: "V", text: "Seek appropriate guidance about the concern and whose wishes or boundaries need attention." },
      { letter: "D", theme: "X", text: "Ask anonymously in a carer group, leaving out names and locations so you can get several useful opinions." },
      { letter: "E", theme: "N", text: "Use an appropriate confidential route, checking which details are necessary and which might identify the person." },
    ],
  },
];

export const TYPE_ORDER = ["R", "O", "N", "V"];

export const TYPE_INFO = {
  R: {
    id: "reassurer",
    name: "The Reassurer",
    lean: "You tend to begin with the relationship: how someone feels, whether they trust you and what will help them feel comfortable.",
    strength: "Warmth, patience and emotional support that can make everyday care feel safer and more personal.",
    watchFor: "Avoiding a difficult conversation because you do not want to upset someone. Reassurance should accompany honest reporting and clear boundaries.",
    tryNext: "Express one necessary boundary kindly and clearly, without softening it until the meaning disappears.",
  },
  O: {
    id: "organiser",
    name: "The Organiser",
    lean: "You tend to look for a workable plan: what needs doing, who is responsible and which arrangements will help the day run reliably.",
    strength: "Clear handovers, practical preparation and dependable routines.",
    watchFor: "Treating the timetable as fixed when the person's wishes or needs have changed. Organisation should make room for the person.",
    tryNext: "Ask which part of the routine they would like to keep, change or do more independently.",
  },
  N: {
    id: "observer",
    name: "The Observer",
    lean: "You tend to notice details, ask questions and compare what is happening with what you would usually expect.",
    strength: "Useful observations, accurate records and thoughtful communication about changes.",
    watchFor: "Waiting for more information when a concern already needs prompt reporting. Observation is not diagnosis.",
    tryNext: "Identify which changes need immediate escalation and which can be followed up through the usual route.",
  },
  V: {
    id: "advocate",
    name: "The Advocate",
    lean: "You tend to begin with the person's voice, informed choices and the boundaries needed for a respectful arrangement.",
    strength: "Speaking up, resisting inappropriate pressure and keeping the person involved in their own care.",
    watchFor: "Becoming so certain of your position that you miss information from someone else. Advocacy includes listening.",
    tryNext: "Begin a difficult conversation with a question, then explain the concern clearly.",
  },
};

// Keyed by `${questionId}${letter}` exactly as referenced in the approved copy (e.g. "1B").
export const X_NOTES = {
  "1B": { label: "Starting with your usual method", text: "Familiarity can help you feel organised, but the person's care plan, preferences and needs must guide your approach. Ask before importing a routine from another placement." },
  "2D": { label: "Taking over to save time", text: "Help can unintentionally remove independence. When the activity is safe, ask what assistance the person wants rather than deciding for them." },
  "3C": { label: "Keeping a handover reassuring", text: "A quiet day can still contain important changes. Pass on relevant observations; reassurance cannot substitute for an accurate handover." },
  "4B": { label: "Defending an established routine", text: "Explain practical constraints, but explore the person's concern before persuading them to accept the existing arrangement." },
  "5D": { label: "Deciding by demonstration", text: "A task looking straightforward does not establish competence or authorisation. Clarify the required training, assessment and care arrangements before undertaking it." },
  "6E": { label: "Focusing on getting others to accept your approach", text: "Explaining your reasoning can be helpful, but repeated disagreements also call for listening and examining your own methods and the working arrangements." },
  "7B": { label: "Correcting a mistake quietly", text: "Address immediate safety and follow the reporting procedure promptly. Someone appearing fine does not establish that the mistake has caused no harm or needs no further action." },
  "8C": { label: "Wanting freedom from discussion", text: "Professional autonomy matters, but it must sit alongside the person's preferences, agreed responsibilities and appropriate accountability." },
  "9D": { label: "Explaining before listening", text: "Your experience may be relevant. First make sure you understand the concern; an explanation should not close down feedback or prevent change." },
  "10D": { label: "Assuming anonymity protects privacy", text: "Removing names and locations may still leave someone identifiable through other details. Use an appropriate confidential advice route rather than assuming an online post is safe." },
};

/**
 * Pure scoring function. Counts R/O/N/V separately; X is counted separately and
 * never folded into the strength totals or subtracted from them.
 * @param {Record<number,string>} answers - { [questionId]: letter }
 */
export function computeResult(answers) {
  const totals = { R: 0, O: 0, N: 0, V: 0 };
  const xHits = [];

  QUESTIONS.forEach((q) => {
    const letter = answers[q.id];
    if (!letter) return;
    const opt = q.options.find((o) => o.letter === letter);
    if (!opt) return;
    if (opt.theme === "X") {
      xHits.push(`${q.id}${letter}`);
    } else {
      totals[opt.theme] += 1;
    }
  });

  const maxTotal = Math.max(totals.R, totals.O, totals.N, totals.V);
  const leadingThemes = maxTotal > 0 ? TYPE_ORDER.filter((t) => totals[t] === maxTotal) : [];
  const totalStrengthAnswers = totals.R + totals.O + totals.N + totals.V;
  const fewStrengthAnswers = leadingThemes.length > 0 && totalStrengthAnswers <= 3;

  return {
    totals,
    leadingThemes,
    xHits,
    allX: leadingThemes.length === 0,
    fewStrengthAnswers,
  };
}
